import { type TypedDocumentNode, useLazyQuery, useQuery } from '@apollo/client'
import { gql } from '__generated__'
import { type SearchFacetInput, type SortField } from '__generated__/graphql'
import { createContext, useState, type ReactNode } from 'react'
import { useRosterSearchUrlState } from 'shared/hooks/rosterSearchUrlState'

export const GetFilteredRosters = gql(/* GraphQL */ `
    query GetFilteredRosters(
        $facets: [SearchFacetInput!]!
        $limit: Int!
        $sort: [SortField!]!
        $start: Int!
        $useSqlSearch: Boolean
    ) {
        rosterSearch(
            facets: $facets
            limit: $limit
            sort: $sort
            start: $start
            useSqlSearch: $useSqlSearch
        ) {
            pageInfo {
                itemTotal
                pageNumber
                pageTotal
            }
            rosters: data {
                id
                name
                company {
                    id
                    name
                }
                createdAt
                createdBy {
                    id
                    firstName
                    lastName
                    nickname
                }
                sharedRecruiters {
                    id
                    firstName
                    nickname
                    lastName
                }
                status
                rosterType
                candidateCount
            }
        }
    }
`)

export const GetFilteredRostersWithCandidate = gql(/* GraphQL */ `
    query GetFilteredRostersWithCandidate(
        $candidateId: ID!
        $facets: [SearchFacetInput!]!
        $limit: Int!
        $sort: [SortField!]!
        $start: Int!
        $useSqlSearch: Boolean
    ) {
        rosterSearch: candidateOnTheseRosters(
            candidateId: $candidateId
            facets: $facets
            limit: $limit
            sort: $sort
            start: $start
            useSqlSearch: $useSqlSearch
        ) {
            pageInfo {
                itemTotal
                pageNumber
                pageTotal
            }
            rosters: data {
                permissions {
                    canEdit
                }
                rosterCandidate {
                    id
                    createdAt
                    internalRecruiterNote {
                        note
                    }
                    roster {
                        id
                        rosterName
                        company {
                            id
                            value
                        }
                        pitchNote {
                            note
                        }
                        status
                        createdAt
                    }
                    candidateProcessStatus {
                        processStatus {
                            id
                            status
                            statusLabel
                            processStatusSummaryGroup {
                                id
                                summaryGroup
                                label
                            }
                        }
                    }
                    mostRecentJobBoardResponse {
                        updatedAt
                        createdAt
                        updatedByUser {
                            firstName
                            lastName
                            nickname
                        }
                        createdByUser {
                            firstName
                            lastName
                            nickname
                        }
                        id
                        isInterested
                        notInterestedReason
                        comments
                        officePrefs {
                            companyOfficeId
                            companyOffice {
                                id
                                officeLocation {
                                    value
                                }
                            }
                        }
                        sectorPrefs {
                            sector {
                                id
                                value
                            }
                        }
                        jobBoard {
                            titles {
                                title {
                                    value
                                    id
                                }
                            }
                            sectors {
                                sector {
                                    id
                                    value
                                }
                            }
                            company {
                                value
                            }
                            startDate
                            companyOffices {
                                companyOffice {
                                    id
                                    officeLocation {
                                        value
                                        id
                                    }
                                }
                            }
                            name
                            description
                        }
                    }
                    sourceStatus
                    candidate {
                        recruitingStatus {
                            status
                        }
                    }
                    clientFavorites {
                        id
                        createdAt
                    }
                    candidateProcessStatusHistory {
                        processStatus {
                            id
                            status
                            statusLabel
                            processStatusSummaryGroup {
                                id
                                summaryGroup
                                label
                            }
                        }
                        isActive
                        createdAt
                        updatedAt
                        createdByUser {
                            firstName
                            lastName
                            nickname
                        }
                    }
                }
            }
        }
    }
`)

export const GetFilteredRostersSansCandidate = gql(/* GraphQL */ `
    query GetFilteredRostersSansCandidate(
        $candidateId: ID!
        $facets: [SearchFacetInput!]!
        $limit: Int!
        $sort: [SortField!]!
        $start: Int!
        $useSqlSearch: Boolean
    ) {
        rosterSearch: rostersWithoutGivenCandidate(
            candidateId: $candidateId
            facets: $facets
            limit: $limit
            sort: $sort
            start: $start
            useSqlSearch: $useSqlSearch
        ) {
            pageInfo {
                itemTotal
                pageNumber
                pageTotal
            }
            rosters: data {
                permissions {
                    canEdit
                    canView
                }
                roster {
                    rosterType
                    id
                    company {
                        id
                        value
                    }
                    rosterName
                    rosterStatus: status
                }
            }
        }
    }
`)

export const GetRostersSansCandidateEditableIds = gql(/* GraphQL */ `
    query GetRostersSansCandidateEditableIds(
        $candidateId: ID!
        $facets: [SearchFacetInput!]!
        $sort: [SortField!]!
        $useSqlSearch: Boolean
    ) {
        rostersWithoutGivenCandidateEditableIds(
            candidateId: $candidateId
            facets: $facets
            limit: -1
            sort: $sort
            start: 1
            useSqlSearch: $useSqlSearch
        )
    }
`)

export const GetRostersWithCandidateEditableIds = gql(/* GraphQL */ `
    query GetRostersWithCandidateEditableIds(
        $candidateId: ID!
        $facets: [SearchFacetInput!]!
        $sort: [SortField!]!
        $useSqlSearch: Boolean
    ) {
        candidateOnTheseRostersEditableIds(
            candidateId: $candidateId
            facets: $facets
            limit: -1
            sort: $sort
            start: 1
            useSqlSearch: $useSqlSearch
        )
    }
`)

export interface RosterTableState {
    limit: number
    page: number
    filters: SearchFacetInput[]
    sort: SortField[]
}

interface RosterSearchContextValue {
    tableState: RosterTableState
    setTableState: (partial: Partial<RosterTableState>) => void
    data: unknown[] | undefined
    isLoading: boolean
    totalItems: number
    totalPages: number
    refetch: () => void
    selectedRosterIds: string[]
    setSelectedRosterIds: (ids: string[]) => void
    selectAllEditableRosters: () => Promise<void>
    isSelectingAll: boolean
    editableTotalCount: number | null
}

export const RosterSearchContext = createContext<RosterSearchContextValue>(
    {} as RosterSearchContextValue
)

interface ProviderProps {
    query: TypedDocumentNode<any, any>
    candidateId?: string
    children?: ReactNode
    useSqlSearch?: boolean
}

export const RosterSearchProvider = ({
    query,
    candidateId,
    children,
    useSqlSearch = false,
}: ProviderProps) => {
    const urlState = useRosterSearchUrlState()
    const [selectedRosterIds, setSelectedRosterIds] = useState<string[]>([])
    const [editableTotalCount, setEditableTotalCount] = useState<number | null>(
        null
    )

    const tableState: RosterTableState = {
        limit: urlState.limit,
        page: urlState.page,
        filters: urlState.graphQLFilters,
        sort: urlState.graphQLSort,
    }

    const setTableState = (partial: Partial<RosterTableState>) => {
        if (partial.limit !== undefined) urlState.setLimit(partial.limit)
        if (partial.page !== undefined) urlState.setPage(partial.page)
        if (partial.filters !== undefined)
            urlState.setGraphQLFilters(partial.filters)
        if (partial.sort !== undefined) urlState.setGraphQLSort(partial.sort)
    }

    const queryVariables = {
        facets: tableState.filters,
        sort: tableState.sort,
        start: tableState.page,
        limit: tableState.limit,
        useSqlSearch,
        ...(candidateId && { candidateId }),
    }

    const {
        data: queryData,
        loading,
        previousData,
        refetch,
    } = useQuery(query, {
        variables: queryVariables,
    })

    const isOnTable = query === GetFilteredRostersWithCandidate

    const [fetchSansEditableIds, { loading: isSelectingSans }] = useLazyQuery(
        GetRostersSansCandidateEditableIds,
        { fetchPolicy: 'network-only' }
    )

    const [fetchWithEditableIds, { loading: isSelectingWith }] = useLazyQuery(
        GetRostersWithCandidateEditableIds,
        { fetchPolicy: 'network-only' }
    )

    const isSelectingAll = isSelectingSans || isSelectingWith

    const selectAllEditableRosters = async () => {
        if (!candidateId) return

        const variables = {
            candidateId,
            facets: tableState.filters,
            sort: tableState.sort,
            useSqlSearch,
        }

        if (isOnTable) {
            const result = await fetchWithEditableIds({ variables })
            if (result.data?.candidateOnTheseRostersEditableIds) {
                const ids = result.data.candidateOnTheseRostersEditableIds
                setSelectedRosterIds(ids)
                setEditableTotalCount(ids.length)
            }
        } else {
            const result = await fetchSansEditableIds({ variables })
            if (result.data?.rostersWithoutGivenCandidateEditableIds) {
                const ids = result.data.rostersWithoutGivenCandidateEditableIds
                setSelectedRosterIds(ids)
                setEditableTotalCount(ids.length)
            }
        }
    }

    const effectiveData = queryData ?? previousData
    const data = effectiveData?.rosterSearch?.rosters
    const pageInfo = effectiveData?.rosterSearch?.pageInfo

    return (
        <RosterSearchContext.Provider
            value={{
                tableState,
                setTableState,
                data,
                isLoading: loading,
                totalItems: pageInfo?.itemTotal ?? 0,
                totalPages: pageInfo?.pageTotal ?? 0,
                refetch: () => refetch(),
                selectedRosterIds,
                setSelectedRosterIds,
                selectAllEditableRosters,
                isSelectingAll,
                editableTotalCount,
            }}
        >
            {children}
        </RosterSearchContext.Provider>
    )
}
